<template>
  <form @submit.prevent="handleSubmit">

    <div class="card-panel">
      <div class="input-field">
        <input type="text" v-model="name" />
        <label class="active teal-text" for="name">Dependant Name</label>
      </div>
    </div>

    <div class="card-panel">
      <div class="row">
        <div class="col s10">
          <div class="file-field input-field">
            <div class="btn grey">
              <span>Change</span>
              <input type="file" @change="handleFile" />
            </div>
            <div class="file-path-wrapper">
              <input class="file-path" v-model="inputFile" type="text" />
              <p v-if="fileError">{{ fileError }}</p>
            </div>
          </div>
        </div>
        <div class="col s2">
          <LoadingSmallDiv v-if="isLoading" />
          <span v-else>
            <img alt=""
              :src="img"
              width="64"
              class="circle responsive-img"
              v-if="img"
            />
            <span v-else>
              <img alt=""
                :src="dependant.profile_img"
                width="64"
                class="circle responsive-img"
                v-if="dependant.profile_img"
              />
              <img alt=""
                :src="`https://ui-avatars.com/api/?name=${dependant.name}&size=128`"
                width="64"
                class="circle responsive-img"
                v-else
              />
            </span>
          </span>
        </div>
      </div>
    </div>

    <div class="card-panel">
      <div class="input-field">
        <label class="active teal-text" for="sex">Sex</label>
        <select id="sex" v-model="sex" class="validate" required>
          <option value="m">Male</option>
          <option value="f">Female</option>
        </select>
      </div>
    </div>

    <div class="card-panel">
      <div class="input-field">
        <input type="text" v-model="dob" class="datepicker validate" required />
        <label class="active teal-text" for="dob">Date of Birth</label>
      </div>
      <div class="age-policy-note" v-if="ageError">
        <i class="material-icons">policy</i>
        <span>{{ ageError }}</span>
      </div>
    </div>

    <div class="card-panel">
      <div class="input-field">
        <input type="text" v-model="phone" class="validate" />
        <label class="active teal-text" for="phone">Phone</label>
      </div>
    </div>

    <div class="card-panel">
      <div class="input-field">
        <input type="email" v-model="email" class="validate" v-if="allowEmailChange" />
        <input type="email" v-model="email" disabled v-else />
        <label class="active teal-text" for="email">Email</label>
      </div>
    </div>

    <div class="card-panel">
      <div class="input-field">
        <input type="text" v-model="address" class="validate" />
        <label class="active teal-text" for="address">Address</label>
      </div>
    </div>

    <div class="card-content">
      <p v-if="uploadError"><br />{{ uploadError }}</p>
      
      <p>
        <br />
        <a
          href="#"
          class="text-back-action"
          :class="{
            disabled: isSubmitting || ageError,
          }"
          @click.prevent="handleBack"
        >
          <i class="material-icons">arrow_back</i>Back
        </a>
        <button
          class="waves-effect waves-light btn-large"
          :class="{
            disabled: isSubmitting,
          }"
        >
          <i class="material-icons right">check_circle</i
          >{{ isSubmitting ? "Updating" : "Update" }}
        </button>
      </p>
    </div>
  </form>
</template>
    
<script>
import { computed, onMounted, onUpdated, ref, watchEffect } from "vue";
import putItem from "@/composables/putItem";
import useStorage from "@/composables/useStorage";
import LoadingSmallDiv from "@/components/LoadingSmallDiv.vue";
import nativeUi from "@/ui/native-ui";

export default {
  name: "EnrolleeEditDependantOnboardComponent",
  components: { LoadingSmallDiv },
  props: ["dependant", "principal", "policy", "serverDate"],
  emits: ["reset"],
  setup(props, context) {
    const file = ref(null);
    const inputFile = ref(null);
    const fileError = ref(null);
    const name = ref(null);
    const sex = ref(null);
    const dob = ref(null);
    const phone = ref(null);
    const email = ref(null);
    const allowEmailChange = ref(null);
    const address = ref(null);
    const isSubmitting = ref(false);
    const isLoading = ref(false);
    const error = ref(null);
    const img = ref(null);
    const originalDob = ref(null);
    const types = ["image/jpg", "image/jpeg", "image/png", "image/gif"];
    const parseLocalDate = (value) => {
      if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split("-").map(Number);
        return new Date(year, month - 1, day);
      }

      return new Date(value);
    };
    const referenceDate = computed(() => {
      const date = props.serverDate ? parseLocalDate(props.serverDate) : new Date();
      return isNaN(date) ? new Date() : date;
    });
    const dependantAgeLimit = computed(() => Number(props.policy?.dependant_age) || 0);
    const memberAge = computed(() => {
      if (!dob.value) return null;

      const date = parseLocalDate(dob.value);
      if (isNaN(date)) return null;

      const today = referenceDate.value;
      let age = today.getFullYear() - date.getFullYear();
      const monthDiff = today.getMonth() - date.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
        age -= 1;
      }

      return age;
    });
    const ageError = computed(() => {
      if (!dependantAgeLimit.value || memberAge.value === null) return null;
      if (memberAge.value > dependantAgeLimit.value && (!originalAge.value || memberAge.value > originalAge.value)) {
        return `Dependant age exceeds the current policy limit of ${dependantAgeLimit.value} years.`;
      }
      return null;
    });
    const originalAge = computed(() => calculateAge(originalDob.value));

    // storage
    const { uploadFile, uploadProgress, uploadError, data, completed } =
      useStorage();

    // loader
    onMounted(() => {
      name.value = props.dependant.name;
      sex.value = props.dependant.sex;
      originalDob.value = props.dependant.dob;

      dob.value = new Date(props.dependant.dob).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });

      phone.value = props.dependant.phone;
      email.value = props.dependant.email || props.dependant.user?.email;
      allowEmailChange.value = props.dependant.user_created !== "y";
      address.value = props.dependant.address;
    });

    // select
    onMounted(() => {
      // activate select field
      const elemsSelect = document.querySelectorAll("select");
      nativeUi.FormSelect.init(elemsSelect, {});

      // activate date
      const elemsDate = document.querySelectorAll(".datepicker");
      const startDate = '2010-01-01';
      const defaultDate = dob.value ? new Date(dob.value) : new Date(startDate);

      nativeUi.Datepicker.init(elemsDate, {
        onSelect: function (date) {
          dob.value = date;
        },
        yearRange: [new Date().getFullYear() - 100, new Date().getFullYear()],
        defaultDate: defaultDate,
        setDefaultDate: !!defaultDate, // only true if defaultDate is set
      });
    });

    // select
    onUpdated(() => {
      // activate select field
      const elemsSelect = document.querySelectorAll("select");
      nativeUi.FormSelect.init(elemsSelect, {});
    });

    // clear
    const clearForm = () => {
      file.value = null; //clear file value
      inputFile.value = null; // Clear the file input value
      isSubmitting.value = false;
      isLoading.value = false;
      uploadProgress.value = "0%";
    };

    // handle file
    const handleFile = (e) => {
      const selected = e.target.files[0];
      if (selected && types.includes(selected.type)) {
        file.value = selected;
        inputFile.value = file.value.name;
        fileError.value = null;
      } else {
        file.value = null;
        inputFile.value = null;
        fileError.value = "Please select an Image (.JPG, .PNG, .GIF)";
      }
    };

    // handle back
    const handleBack = () => {
      context.emit("reset", null);
    };

    const calculateAge = (value) => {
      if (!value) return null;

      const date = parseLocalDate(value);
      if (isNaN(date)) return null;

      const today = referenceDate.value;
      let age = today.getFullYear() - date.getFullYear();
      const monthDiff = today.getMonth() - date.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
        age -= 1;
      }

      return age;
    };

    //hand
    watchEffect(() => {
      if (file.value) {
        // start ploading
        isLoading.value = true;
        uploadError.value = null;

        // upload image
        uploadFile(file.value);
      }
    });

    //handle submit
    const handleSubmit = async () => {
      // Native form validation handles custom errors \\
      if (ageError.value) {
        error.value = ageError.value;
        return;
      }

      // start
      isSubmitting.value = true;
      error.value = false;
      uploadProgress.value = false;

      // re-format date
      let newDate = new Date(dob.value);
      newDate = newDate
        .toLocaleDateString("en-GB")
        .split("/")
        .reverse()
        .join("-");

      // variable
      const newVal = {
        name: name.value,
        sex: sex.value,
        dob: newDate,
        phone: phone.value,
        address: address.value,
      };
      if (allowEmailChange.value) {
        newVal.email = email.value || null;
      }
      // check new image
      if (img.value) {
        newVal.profile_img = img.value;
      }

      // post item
      const { res, err } = await putItem(
        "dependant",
        props.dependant.id,
        newVal
      );

      if (res.value) {
        // start
        let dependant;
        const principal = { ...props.principal };

        // next
        dependant = {
          ...props.dependant,
          ...res.value.data,
        };

        // merge with principal
        let idx;

        if (dependant) {
          principal.dependants = [...(principal.dependants || [])];
          principal.dependants.forEach((x, y) => {
            if (x.id == dependant.id) {
              idx = y;
            }
          });
          // update
          if (idx !== undefined) {
            principal.dependants[idx] = dependant;
          }
        }

        // go back
        context.emit("reset", principal);
      } else {
        error.value = err.value;
      }

      // end here
      // clear
      clearForm();
    };

    // watch tariff upload
    watchEffect(() => {
      if (completed.value) {
        // set image
        img.value = data.value;
        // clear
        clearForm();
        // data.succ.Success, data.succ.inserted_rows, data.succ.Error
        // console.log(data); // might not be necessary
      }
      if (uploadError.value) {
        // clear
        clearForm();
      }
    });

    return {
      handleFile,
      handleBack,
      handleSubmit,
      isSubmitting,
      isLoading,
      uploadProgress,
      fileError,
      uploadError,
      img,
      file,
      inputFile,
      name,
      sex,
      dob,
      ageError,
      phone,
      email,
      allowEmailChange,
      address,
    };
  },
};
</script>
    
<style scoped>
.text-back-action {
  align-items: center;
  color: #455a64;
  display: inline-flex;
  gap: 5px;
  font-weight: 600;
  margin-right: 14px;
  padding: 12px 0;
}

.text-back-action i {
  font-size: 18px;
}

.text-back-action.disabled {
  opacity: 0.55;
  pointer-events: none;
}

.age-policy-note {
  align-items: center;
  background: #fff3e0;
  border-left: 4px solid #ef6c00;
  border-radius: 4px;
  color: #4e342e;
  display: flex;
  gap: 10px;
  margin-top: 14px;
  padding: 10px 12px;
}

.age-policy-note i {
  color: #ef6c00;
  font-size: 20px;
}
</style>
